import Link from 'next/link';
import useLocale from 'next-translate/useTranslation';
import React from 'react';

import AutoPlayVideo from '@/components/media/AutoPlayVideo';

const MEDIA = 'https://media.easy-peasy.ai/landing/home';
const ASSETS = 'https://media.easy-peasy.ai/assets';

type UseCase = {
  title: string;
  description: string;
  href: string;
  media: string;
  poster?: string;
  video?: boolean;
  wide?: boolean;
};

const USE_CASES: UseCase[] = [
  {
    title: 'Stunning images',
    description: 'Photoreal to anime with 40+ top models. Any style, up to 4K.',
    href: '/ai-image-generator',
    media: 'https://media.easy-peasy.ai/landing/image/hero-samurai.webp',
  },
  {
    title: 'Cinematic video',
    description:
      'Direct scenes with Seedance 2.0, Veo and more. Text in, film out.',
    href: '/ai-video-generator',
    media: `${ASSETS}/seedance2_hero.mp4`,
    poster: `${MEDIA}/seedance2_hero-poster.jpg`,
    video: true,
  },
  {
    title: 'Talking avatars',
    description:
      'Turn any photo into a speaking presenter with perfect lip-sync.',
    href: '/ai-video-generator/talking',
    media: `${ASSETS}/news-anchor.mp4`,
    poster: `${MEDIA}/news-anchor-poster.jpg`,
    video: true,
  },
  {
    title: 'An agent that does the work',
    description:
      'Ask Marky to build a website, research the web, analyze files or create slides — and watch it happen.',
    href: '/marky',
    media: `${ASSETS}/website-1024-hq.mp4`,
    poster: `${MEDIA}/website-1024-hq-poster.jpg`,
    video: true,
    wide: true,
  },
];

const UseCaseCard = ({ useCase }: { useCase: UseCase }) => {
  const { t } = useLocale('home');

  return (
    <Link
      href={useCase.href}
      className={`group relative block overflow-hidden rounded-3xl border border-white/10 bg-white/5 ${
        useCase.wide
          ? 'md:col-span-3 h-[320px] md:h-[420px]'
          : 'h-[320px] md:h-[380px]'
      }`}
    >
      {useCase.video ? (
        <AutoPlayVideo
          src={useCase.media}
          poster={useCase.poster}
          className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
        />
      ) : (
        // eslint-disable-next-line @next/next/no-img-element
        <img
          src={useCase.media}
          alt={useCase.title}
          loading='lazy'
          decoding='async'
          className='absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]'
        />
      )}
      <div className='absolute inset-0 bg-gradient-to-t from-black/90 via-black/35 to-black/5' />
      <div className='absolute inset-x-0 bottom-0 p-6 md:p-8'>
        <h3 className='text-2xl font-extrabold tracking-tight text-white md:text-3xl'>
          {t(useCase.title)}
        </h3>
        <p className='mt-2 max-w-xl text-sm leading-relaxed text-slate-200 md:text-base'>
          {t(useCase.description)}
        </p>
        <span className='mt-4 inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold text-white backdrop-blur-md transition-all group-hover:gap-3 group-hover:bg-indigo-500'>
          {t('Try it now')} <span aria-hidden='true'>→</span>
        </span>
      </div>
    </Link>
  );
};

const UseCaseCards = () => {
  const { t } = useLocale('home');

  return (
    <section className='relative py-20 lg:py-28'>
      <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
        <div className='mb-12 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('What will you make today?')}
          </h2>
          <p className='mx-auto mt-4 max-w-2xl text-lg text-slate-400'>
            {t(
              'From a single prompt to a finished piece — pick a lane and go.',
            )}
          </p>
        </div>
        <div className='grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-5'>
          {USE_CASES.map((useCase) => (
            <UseCaseCard key={useCase.title} useCase={useCase} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default UseCaseCards;
